import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { MatIconModule } from '@angular/material/icon';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ReviewerService } from '../../core/services/reviewer.service';
import { UserRoleService } from '../../core/services/user-role.service';
import { ActionPlanDetailPopupComponent } from './action-plan-detail-popup/action-plan-detail-popup.component';
import { DateFormatPipe } from 'src/app/core/pipes/date-format.pipe';
@Component({
  selector: 'app-issue-view-edit',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterLink, MatIconModule, ActionPlanDetailPopupComponent, DateFormatPipe],
  templateUrl: './issue-view-edit.component.html',
  styleUrl: './issue-view-edit.component.css',
})
export class IssueViewEditComponent implements OnInit {
  private destroyRef = inject(DestroyRef);
  private reviewerService = inject(ReviewerService);
  private userRoleService = inject(UserRoleService);

  issueId = '';
  actionPlanSearch = '';
  isLoading = false;
  isEditMode = false;
  fromViewIcon = false;
  linkedApTouched = false;
  showApproveModal = false;
  showRejectModal = false;
  rejectionReason = '';
  


  private issueNumericId: number | null = null;

  isSubmitting = false;

  actionCompleted = false;

  reviewId: number | null = null;
  isGoLive = false;
  selectedActionPlanId: string | null = null;

  
  get hasSupervisorAccess(): boolean {
    return this.userRoleService.isSupervisor;
  }

  get isReviewer(): boolean {
    return this.userRoleService.isReviewer;
  }

  get isAdmin(): boolean {
    return this.userRoleService.isAdmin;
  }

  get isLinkedApDirty(): boolean {
    return this.fromViewIcon && this.isAdmin && this.linkedApTouched;
  }

  reviewSummary = {
    reviewId: '',
    reviewName: '',
    clientName: '',
    market: '',
    marketUnit: '',
    locationToBeReviewed: '',
    country: '',
    proposedMonth: '',
    planFieldworkStartDate: '',
    planFieldworkEndDate: '',
    rcReviewer: '',
    rcSupervisor: '',
    rcManager: '',
    secondaryRcReviewer: '',
    secondaryRcSupervisor: '',
    secondaryRcManager: '',
    statusOfReview: '',
    singleMultipleLocation: '',
    ScheduleFieldworkStartDate: '',
    ScheduleFieldworkEndDate: '',
  };

  issue = {
    issueTitle: '',
    issueDescription: '',
    rcReviewer: '',
    rcSupervisor: '',
    rcManager: '',
    identificationDate: '',
    subscribers: [] as string[],
    rcComments: '',
    actionRequiredBy: '',
    issueRating: '',
    issueStatus: '',
    reviewObjective: '',
    recommendation: '',
    locationReviewedAndImpacted: [] as string[],
    broadIssueCategoryL1: '',
    detailedIssueCategoryL2: '',
    riskStatementL3: '',
    repLetterConflict: '',
    repLetterQuestionsWithConflict: [] as string[],
    serviceLine: [] as string[],
    maxTargetDate: '',
    actualDateForIssueClosure: '',
    reportStatus: '',
    isThisIssueFrom: '',
    legacyIssueNumber: '',
    uploadedDocuments: [] as { fileName: string; documentId: number; storagePath: string }[],
    createdBy: '',
    secondarySupervisor: '',
    secondaryRcReviewer: '',
    secondaryRcManager: '',
  };

  linkedActionPlans: {
    id: string;
    description: string;
    owner: string;
    targetDate: string;
    status: string;
    linked: boolean;
  }[] = [];

  /** Furthest Target Date among all linked Action Plans (any status); empty when none, same as Step 1. */
  get issueCloseDueDate(): string {
    const dates = this.linkedActionPlans.map((ap) => String(ap.targetDate ?? '').slice(0, 10)).filter((d) => /^\d{4}-\d{2}-\d{2}$/.test(d));
    return dates.length ? dates.reduce((a, b) => (b > a ? b : a)) : '';
  }

  constructor(private route: ActivatedRoute) {}

  ngOnInit(): void {
    this.issueId = this.route.snapshot.paramMap.get('issueId') ?? '';
    this.isEditMode =
      history.state?.['mode'] === 'edit' ||
      this.route.snapshot.queryParamMap.get('mode') === 'edit';
    this.fromViewIcon = history.state?.['fromViewIcon'] === true ||
      this.route.snapshot.queryParamMap.get('fromViewIcon') === '1';
    const numericId = parseInt(this.issueId.replace(/^I-/i, ''), 10);
    this.issueNumericId = isNaN(numericId) ? null : numericId;
    const qpReviewId = this.route.snapshot.queryParamMap.get('reviewId');
    const reviewId: number | null = history.state?.['reviewId']
      ?? (qpReviewId ? Number(qpReviewId) : null);
    this.reviewId = reviewId;
    this.isGoLive = history.state?.['isGoLive'] === true ||
      this.route.snapshot.queryParamMap.get('isGoLive') === '1';
    const linkedActionPlanFromGrid: string | null = history.state?.['linkedActionPlan'] ??
      this.route.snapshot.queryParamMap.get('linkedActionPlan') ?? null;
    if (!isNaN(numericId) && reviewId != null) {
      this.loadIssueDetails(reviewId, numericId, linkedActionPlanFromGrid);
    }
  }

  private loadIssueDetails(reviewId: number, issueId: number, linkedActionPlanFromGrid: string | null): void {
    this.isLoading = true;
    this.reviewerService.getIssueEAV(reviewId, issueId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response: any) => {
          const list = Array.isArray(response)
            ? response
            : (response?.issues ?? response?.Issues ?? response?.Issue ?? [response]);
          const i = Array.isArray(list) ? list[0] : list;
          if (i) {
            this.mapIssueData(i, reviewId, linkedActionPlanFromGrid);
          }
          // Fall back to localStorage cache if EAV returned no documents.
          if (!this.issue.uploadedDocuments.length) {
            try {
              const lsDocs = localStorage.getItem(`ast_docs_${issueId}`);
              if (lsDocs) {
                const arr = JSON.parse(lsDocs);
                if (Array.isArray(arr) && arr.length) {
                  this.issue.uploadedDocuments = arr.map((d: any) => ({
                    fileName: d.FileName ?? '',
                    documentId: d.DocumentID ?? d.IssueDocumentID ?? 0,
                    storagePath: d.StorageFilePath ?? '',
                  }));
                }
              }
            } catch {}
          }
          this.isLoading = false;
          // Fetch full AP details via EAV since getIssueEAV does not return ActionPlans array.
          this.reviewerService.getActionPlansEAV(reviewId)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
              next: (apResponse: any) => {
                const apList = Array.isArray(apResponse) ? apResponse : [];
                const issueNumericStr = String(issueId);
                let authorizedIds: Set<string> | null = null;
                try {
                  const lsLinked = localStorage.getItem(`ast_linked_aps_${issueNumericStr}`);
                  if (lsLinked !== null) {
                    const arr = JSON.parse(lsLinked);
                    const ids = Array.isArray(arr) ? arr.map((id: any) => String(id).trim()).filter(Boolean) : [];
                    if (ids.length > 0) authorizedIds = new Set(ids);
                  }
                } catch {}
                // Fall back to the grid row value when no localStorage cache exists (mirrors mapIssueData logic).
                if (authorizedIds === null && linkedActionPlanFromGrid) {
                  const gridIds = String(linkedActionPlanFromGrid).split(',').map((s: string) => s.replace(/^AP-/i, '').trim()).filter(Boolean);
                  if (gridIds.length > 0) authorizedIds = new Set(gridIds);
                }
                // Skip overwrite if localStorage cache already populated linkedActionPlans.
                try {
                  const lsSaved = localStorage.getItem(`ast_ive_state_${issueNumericStr}`);
                  if (lsSaved !== null) {
                    const saved = JSON.parse(lsSaved);
                    if (Array.isArray(saved) && saved.length > 0) return;
                  }
                } catch {}
                const filtered = authorizedIds !== null
                  ? apList.filter((ap: any) => authorizedIds!.has(String(ap.ActionPlanID ?? '').trim()))
                  : [];
                if (filtered.length > 0) {
                  this.linkedActionPlans = filtered.map((ap: any) => ({
                    id: ap.ActionPlanID ? `AP-${ap.ActionPlanID}` : '',
                    description: ap.Description || ap.AP_Description || ap.ActionPlanDescription || '',
                    owner: ap.OwnerEmail || ap.AP_OwnerEmail || '',
                    targetDate: ap.TargetDate || ap.TargetDateForActionPlanRemediationAction || ap.AP_TargetDate || '',
                    status: ap.Status || ap.AP_Status || ap.ActionPlanStatus || '',
                    linked: true,
                  }));
                }
              },
              error: () => {},
            });
        },
        error: (err) => {
          console.error('GetIssue error:', err?.status, err?.message);
          this.isLoading = false;
        },
      });
  }

  private mapIssueData(i: any, reviewId: number, linkedActionPlanFromGrid: string | null = null): void {
    this.reviewSummary = {
      reviewId: String(reviewId),
      reviewName: i.ReviewName ?? '',
      clientName: i.Review_ClientName ?? i.Review_MasterClientName ?? i.ClientName ?? '',
      market: i.Review_Market ?? i.Market ?? '',
      marketUnit: i.Review_MarketUnit ?? i.MarketUnit ?? '',
      locationToBeReviewed: i.Review_LocationToBeReviewed ?? i.Review_LocationName ?? '',
      country: i.Review_DeliveryCountry ?? i.DeliveryCountry ?? '',
      proposedMonth: i.Review_ProposedMonth ?? i.ProposedMonth ?? '',
      planFieldworkStartDate: i.Schedule_PlanFieldworkStartDate ?? i.Plan_FieldWorkStartDate ?? '',
      planFieldworkEndDate: i.Schedule_PlanFieldworkEndDate ?? i.Plan_FieldWorkEndDate ?? '',
      rcReviewer: i.Review_RCReviewer ?? '',
      rcSupervisor: i.Review_RCSupervisor ?? '',
      rcManager: i.Review_RCManager ?? '',
      secondaryRcReviewer: i.Review_secondaryRCReviewer ?? i.Review_SecondaryRCReviewer ?? '',
      secondaryRcSupervisor: i.Review_secondaryRCSupervisor ?? i.Review_SecondaryRCSupervisor ?? '',
      secondaryRcManager: i.Review_secondaryRCManager ?? i.Review_SecondaryRCManager ?? '',
      statusOfReview: i.CurrentStatus ?? i.Status ?? '',
      singleMultipleLocation: i.Review_SingleMultipleLocation ?? i.SingleorMultipleLocation ?? '',
      ScheduleFieldworkStartDate: i.Schedule_PlanFieldworkStartDate ?? i.ScheduleFieldworkStartDate ?? '',
      ScheduleFieldworkEndDate: i.Schedule_PlanFieldworkEndDate ?? i.ScheduleFieldworkEndDate ?? '',
    };

    this.issue = {
      issueTitle: i.IssueTitle ?? '',
      issueDescription: i.IssueDescription ?? '',
      rcReviewer: i.RCReviewer ?? i.Review_RCReviewer ?? '',
      rcSupervisor: i.RCSupervisor ?? i.Review_RCSupervisor ?? '',
      rcManager: i.RCManager ?? i.Review_RCManager ?? '',
      identificationDate: i.IdentificationDate ?? '',
      subscribers: this.mapSubscribers(i.SubscriberNames ?? i.Subscribers ?? i.IssueSubscribersList),
      rcComments: i.RCComments ?? '',
      actionRequiredBy: i.ActionRequiredBy ?? '',
      issueRating: i.IssueRating ?? '',
      issueStatus: i.IssueStatus ?? '',
      reviewObjective: i.ReviewObjective ?? '',
      recommendation: i.Recommendation ?? '',
      locationReviewedAndImpacted: this.toStringArray(i.Locations ?? i.LocationNames ?? i.IssueLocationsList ?? i.LocationReviewedImpacted),
      broadIssueCategoryL1: i.BroadIssueCategoryL1 ?? i.BroadIssueCategory ?? '',
      detailedIssueCategoryL2: i.DetailedIssueCategoryL2 ?? i.DetailedIssueCategory ?? '',
      riskStatementL3: i.RiskStatementL3 ?? '',
      repLetterConflict: (() => {
        const v = i.RepLetterConflict;
        const lv = String(v ?? '').toLowerCase();
        if (v === true  || lv === 'true'  || lv === 'yes') return 'Yes';
        if (v === false || lv === 'false' || lv === 'no')  return 'No';
        if (lv === 'not applicable') return 'Not Applicable';
        return '';
      })(),
      repLetterQuestionsWithConflict: this.toStringArray(i['RepLetter Questions with Conflict'] ?? i.RepLetterQuestions),
      serviceLine: this.toStringArray(i['Accenture Operation Service Line'] ?? i.ServiceLine),
      maxTargetDate: i.IssueCloseDueDate ?? i.MaxTargetDate ?? '',
      actualDateForIssueClosure: i.ActualClosureDate ?? '',
      reportStatus: i.ReportStatus ?? '',
      isThisIssueFrom: i.IssueFromGoLive ?? i.IsThisIssueFrom ?? '',
      legacyIssueNumber: i.LegacyIssueNumber ?? '',
      uploadedDocuments: (() => {
        const docsRaw = i.IssueDocumentsList ?? i.Documents ?? i.DocumentsJSON;
        const docsArr = typeof docsRaw === 'string'
          ? (() => { try { return JSON.parse(docsRaw); } catch { return []; } })()
          : (Array.isArray(docsRaw) ? docsRaw : []);
        return docsArr.map((d: any) => ({ fileName: d.FileName ?? '', documentId: d.IssueDocumentID ?? d.DocumentID ?? 0, storagePath: d.StorageFilePath ?? '' }));
      })(),
      createdBy: i.CreatedBy ?? '',
      secondarySupervisor: i.Review_SecondaryRCSupervisor ?? i.Review_SecondaryRcSupervisor ?? i.SecondaryRCSupervisor ?? this.issue.secondarySupervisor ?? '',
      secondaryRcReviewer: i.Review_secondaryRCReviewer ?? i.Review_SecondaryRCReviewer ?? '',
      secondaryRcManager: i.Review_secondaryRCManager ?? i.Review_SecondaryRCManager ?? '',
    };

    const plans = Array.isArray(i.IssueActionPlansList) ? i.IssueActionPlansList : (Array.isArray(i.ActionPlans) ? i.ActionPlans : []);
    // Determine authoritative linked AP IDs: localStorage cache → grid row value → API data.
    // The GetIssue API has a backend JOIN bug that returns all APs in the review as linked.
    const issueNumericStr = String(i.IssueID ?? i.IssueId ?? '').replace(/^[A-Za-z]+-/i, '').trim();
    let authorizedIds: Set<string> | null = null;
    try {
      const lsLinked = issueNumericStr ? localStorage.getItem(`ast_linked_aps_${issueNumericStr}`) : null;
      if (lsLinked !== null) {
        const arr = JSON.parse(lsLinked);
        const ids = Array.isArray(arr) ? arr.map((id: any) => String(id).trim()).filter(Boolean) : [];
        if (ids.length > 0) authorizedIds = new Set(ids);
      }
    } catch {}
    if (authorizedIds === null && linkedActionPlanFromGrid !== null) {
      const gridIds = String(linkedActionPlanFromGrid).split(',').map((s: string) => s.replace(/^AP-/i, '').trim()).filter(Boolean);
      authorizedIds = new Set(gridIds);
    }
    const filteredPlans = authorizedIds !== null
      ? plans.filter((ap: any) => authorizedIds!.has(String(ap.ActionPlanID ?? '').trim()))
      : plans;
    this.linkedActionPlans = filteredPlans.map((ap: any) => ({
      id: ap.ActionPlanName ?? (ap.ActionPlanID ? `AP-${ap.ActionPlanID}` : ''),
      description: ap.AP_Description ?? ap.Description ?? ap.ActionPlanDescription ?? '',
      owner: ap.AP_OwnerEmail ?? ap.OwnerEmail ?? '',
      targetDate: ap.AP_TargetDate ?? ap.TargetDate ?? ap.TargetDateForActionPlanRemediationAction ?? '',
      status: ap.AP_Status ?? ap.ActionPlanStatus ?? ap.Status ?? '',
      linked: true,
    }));
    // Actual Date for Issue Closure = latest "Closed date for the action plan" among linked APs (only when issue is Closed).
    if (String(this.issue.issueStatus ?? '').trim().toLowerCase().startsWith('close')) {
      let latest = 0;
      let latestRaw = '';
      for (const ap of filteredPlans) {
        const raw = ap.AP_ClosedDate ?? ap.ClosedDateForActionPlan ?? ap.ClosedDate ?? '';
        const t = raw ? new Date(raw).getTime() : NaN;
        if (!isNaN(t) && t > latest) { latest = t; latestRaw = raw; }
      }
      if (latestRaw) this.issue.actualDateForIssueClosure = latestRaw;
    } else {
      this.issue.actualDateForIssueClosure = '';
    }
    try {
      const lsSaved = issueNumericStr ? localStorage.getItem(`ast_ive_state_${issueNumericStr}`) : null;
      if (lsSaved !== null) {
        const saved = JSON.parse(lsSaved);
        if (Array.isArray(saved) && saved.length > 0) {
          this.linkedActionPlans = saved;
        }
      }
    } catch {}
  }

  private mapSubscribers(value: any): string[] {
    if (Array.isArray(value)) {
      return value
        .map((s: any) => (typeof s === 'string' ? s : (s?.IssueSubscriber ?? '')))
        .map((s: string) => s.trim())
        .filter(Boolean);
    }
    return this.toStringArray(value);
  }

  private toStringArray(value: any): string[] {
    if (Array.isArray(value)) return value.map(String);
    if (typeof value === 'string' && value.trim()) {
      return value.split(',').map((s) => s.trim()).filter(Boolean);
    }
    return [];
  }

  get filteredActionPlans() {
    const isClosed = this.issue.issueStatus?.toLowerCase() === 'close';
    const plans = isClosed
      ? this.linkedActionPlans.filter(ap => ap.status.toLowerCase() === 'closed')
      : this.linkedActionPlans;
    const term = this.actionPlanSearch.trim().toLowerCase();
    if (!term) return plans;
    return plans.filter(
      (ap) =>
        ap.id.toLowerCase().includes(term) ||
        ap.description.toLowerCase().includes(term) ||
        ap.owner.toLowerCase().includes(term) ||
        ap.targetDate.toLowerCase().includes(term) ||
        ap.status.toLowerCase().includes(term),
    );
  }

  getRatingClass(rating: string): string {
    switch (rating?.toLowerCase()) {
      case 'red':    return 'rating-red';
      case 'yellow': return 'rating-yellow';
      case 'blue':   return 'rating-blue';
      case 'green':  return 'rating-green';
      default:       return 'rating-not-rated';
    }
  }

  get showRoutingNote(): boolean {
    const toEid = (v: string | undefined | null) => (v ?? '').toLowerCase().split('@')[0];
    const currentEid = toEid(this.userRoleService.enterpriseId);
    const supervisorCreatedIssue = !!this.issue.createdBy && toEid(this.issue.createdBy) === toEid(this.issue.rcSupervisor);
    const result =
      this.isEditMode &&
      this.isInactiveStatus &&
      !!currentEid &&
      currentEid === toEid(this.reviewSummary.rcSupervisor) &&
      !this.issue.secondarySupervisor &&
      supervisorCreatedIssue;
    return result;
  }

  get isDraft(): boolean {
    return this.issue.issueStatus?.toLowerCase() === 'draft';
  }

  get isInactiveStatus(): boolean {
    return this.issue.issueStatus?.toLowerCase().replace(/[^a-z]/g, '') === 'inactive';
  }

  get isApprovedReadOnly(): boolean {
    const s = (this.issue.issueStatus ?? '').toLowerCase().trim();
    return s === 'open' || s === 'closed';
  }


  get canApproveReject(): boolean {
    if (!this.isEditMode || !this.isInactiveStatus) return false;

    const enterpriseId = this.userRoleService.enterpriseId?.toLowerCase();
    if (!enterpriseId) return false;

    const toEid = (v: string | undefined | null) => (v ?? '').toLowerCase().split('@')[0];
    // Prefer review-level data (always current) over issue-level data (snapshot at creation, may be stale after edits).
    const rcSupervisorEid        = toEid(this.reviewSummary.rcSupervisor || this.issue.rcSupervisor);
    const rcManagerEid           = toEid(this.reviewSummary.rcManager    || this.issue.rcManager);
    const secondarySupervisorEid = toEid(this.reviewSummary.secondaryRcSupervisor || this.issue.secondarySupervisor);
    const createdByEid           = toEid(this.issue.createdBy);

    // Creator is always blocked from approving/rejecting their own issue.
    if (!!this.issue.createdBy && createdByEid === enterpriseId) return false;

    // CASE 1: Manager created the issue → route to primary supervisor (by assignment, not role).
    const managerCreatedIssue = !!this.issue.createdBy && createdByEid === rcManagerEid;
    if (managerCreatedIssue) {
      return enterpriseId === rcSupervisorEid;
    }

    // CASE 2: Primary supervisor created the issue → route to secondary if assigned, else manager.
    const supervisorCreatedIssue = !!this.issue.createdBy && createdByEid === rcSupervisorEid;
    if (supervisorCreatedIssue) {
      if (this.issue.secondarySupervisor) {
        return enterpriseId === secondarySupervisorEid;
      }
      return enterpriseId === rcManagerEid;
    }

    // CASE 3: Issue created by reviewer, secondary supervisor, or anyone else.
    //   Primary supervisor approves (by assignment, not role).
    return enterpriseId === rcSupervisorEid;
  }

  downloadDocument(doc: { storagePath: string; fileName: string }): void {
    this.reviewerService.downloadIssueDocument(doc.storagePath)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (blob: Blob) => {
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = doc.fileName || doc.storagePath.split('/').pop() || 'download';
          a.click();
          URL.revokeObjectURL(url);
        },
        error: (err: any) => console.error('Download error:', err?.status, err?.message),
      });
  }

  cancel(): void {
    window.close();
    // Fallback if browser prevents closing (tab not opened by script)
    sessionStorage.setItem('expandIssueManagement', 'true');
    history.back();
  }

  onLinkedApChange(): void {
    this.linkedApTouched = true;
  }

  openActionPlan(ap: { id: string }): void {
    const numeric = String(ap?.id ?? '').replace(/^AP-/i, '').trim();
    if (numeric) {
      this.selectedActionPlanId = numeric;
    }
  }

  closeActionPlanPopup(): void {
    this.selectedActionPlanId = null;
  }

  update(): void {
    if (!this.isLinkedApDirty || this.issueNumericId == null || this.reviewId == null || this.isLoading) return;
    this.isLoading = true;
    const issueId = this.issueNumericId;
    const reviewId = this.reviewId;
    const modifiedBy = this.userRoleService.email ?? '';
    this.reviewerService.updateActionPlanLink({
      params: {
        category: 'UpdateActionPlanlink',
        ActionPlanID: null,
        IssueID: issueId,
        ReviewID: reviewId,
        ModifiedBy: modifiedBy,
      },
    })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          try { localStorage.setItem(`ast_ive_state_${issueId}`, JSON.stringify(this.linkedActionPlans)); } catch {}
          try { localStorage.setItem('ast_issue_updated', Date.now().toString()); } catch {}
          this.isLoading = false;
          window.close();
          history.back();
        },
        error: (err: any) => {
          console.error('UpdateActionPlanlink error:', err?.status, err?.message);
          this.isLoading = false;
        },
      });
  }

  approve(): void {
    this.showApproveModal = true;
  }

  closeApproveModal(): void {
    this.showApproveModal = false;
  }

  confirmApprove(): void {
    const isBlue = this.issue.issueRating?.toLowerCase() === 'blue';
    const linkedAps = this.linkedActionPlans.filter(ap => ap.linked);
    const allLinkedApsClosed =
      linkedAps.length > 0 && linkedAps.every(ap => ap.status?.toLowerCase() === 'closed');
    const newStatus = isBlue || (allLinkedApsClosed && this.isInactiveStatus) ? 'Closed' : 'Open';
    this.submitIssueStatus(newStatus);
  }

  reject(): void {
    this.rejectionReason = '';
    this.showRejectModal = true;
  }

  closeRejectModal(): void {
    this.showRejectModal = false;
  }

  confirmReject(): void {
    if (!this.rejectionReason.trim()) {
      return;
    }
    this.submitIssueStatus('Rejected', this.rejectionReason.trim());
  }

  /** Calls UpdateIssueStatus and, on success, updates the local status and enters post-action state. */
  private submitIssueStatus(newStatus: string, rejectionReason?: string): void {
    if (this.issueNumericId == null || this.reviewId == null || this.isSubmitting) {
      return;
    }
    // Close the confirmation popup and show the loading spinner while the API call runs.
    this.showApproveModal = false;
    this.showRejectModal = false;
    this.isSubmitting = true;
    this.isLoading = true;
    this.reviewerService.updateIssueStatus({
      IssueID: this.issueNumericId,
      ReviewID: this.reviewId,
      IssueStatus: newStatus,
      LoggedUserID: this.userRoleService.email,
      ReasonForReject: rejectionReason ?? null,
    })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          // Status change hides the Approve/Reject buttons (canApproveReject is no longer inactive).
          this.issue.issueStatus = newStatus;
          this.actionCompleted = true;
          this.isSubmitting = false;
          this.isLoading = false;
        },
        error: (err: any) => {
          console.error('UpdateIssueStatus error:', err?.status, err?.message);
          this.isSubmitting = false;
          this.isLoading = false;
        },
      });
  }
}
